<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  options: { type: Array, default: () => [] },
});

const emit = defineEmits(['submit', 'cancel']);
const { t, te } = useI18n();
const state = reactive({ inboxId: '' });
const error = ref('');

const hasOptions = computed(() => props.options.length > 0);
const selected = computed(() =>
  props.options.find(option => Number(option.value) === Number(state.inboxId))
);
const conflicts = computed(() => selected.value?.conflicts || []);
const conflictWarning = computed(() => {
  if (!conflicts.value.length) return '';

  const names = conflicts.value
    .map(kind => {
      const key = `ARROWS_AI.INBOXES.CONFLICT_${kind.toUpperCase()}`;
      return te(key) ? t(key) : kind;
    })
    .join(', ');

  return t('ARROWS_AI.INBOXES.CONFLICT_WARNING', { names });
});
const fieldMessage = computed(() => {
  if (error.value) return error.value;
  if (!hasOptions.value) return t('ARROWS_AI.INBOXES.NO_AVAILABLE');
  return '';
});

const handleSubmit = () => {
  if (!state.inboxId) {
    error.value = t('ARROWS_AI.INBOXES.FORM_ERROR');
    return;
  }

  emit('submit', Number(state.inboxId));
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('ARROWS_AI.INBOXES.FORM_LABEL') }}
      </label>
      <ComboBox
        v-model="state.inboxId"
        :options="options"
        :has-error="Boolean(error)"
        :placeholder="t('ARROWS_AI.INBOXES.FORM_PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6 [&_ul]:!max-h-48"
        :message="fieldMessage"
        @update:model-value="error = ''"
      />
    </div>
    <p
      v-if="conflictWarning"
      class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-2 text-n-slate-12"
    >
      {{ conflictWarning }}
    </p>
    <div class="flex items-center w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        :label="t('ARROWS_AI.CANCEL')"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        class="w-full"
        :label="t('ARROWS_AI.INBOXES.CONNECT')"
        :disabled="!hasOptions"
      />
    </div>
  </form>
</template>
